import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ChevronLeft, Utensils, Plus, Trash2, Save, Wand2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  generalj, generalHonap, KONYHAK, NAPROV, SLOTS,
  toPersistable, fromPersistable, osszeg, emakro, alkalmazottHozz,
} from '../lib/mealGenerator';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';
const API = process.env.REACT_APP_BACKEND_URL;
const GOAL_MAP = { fat_loss: 'Fogyás', maintenance: 'Szintentartás', muscle_gain: 'Izomépítés' };
const GOAL_INV = { 'Fogyás': 'fat_loss', 'Szintentartás': 'maintenance', 'Izomépítés': 'muscle_gain' };

// Mirrors backend MACRO_ACTIVITY_FACTOR (routes/calculator.py:542) 1:1.
const ACTIVITY_MAP = {
  sedentary_office: { label: 'Ülőmunka (napi 6+ óra ülés)', factor: 1.2 },
  standing_light:   { label: 'Állómunka / könnyű fizikai',  factor: 1.4 },
  heavy_shift:      { label: '12h műszak / nehéz fizikai',  factor: 1.7 },
};

const hu = n => Math.round(n).toLocaleString('hu-HU');

const DEFAULT_CFG = () => ({
  diet: { veget: false, vegan: false, laktoz: false },
  konyhak: [], feherjedus: false,
  edzes: [true, false, true, false, true, false, false],
});

export default function AdminMealPlanEditorPage() {
  const nav = useNavigate();
  const adminKey = localStorage.getItem(ADMIN_KEY_STORAGE) || '';
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [target, setTarget] = useState({ kcal: 2200, protein_g: 140, carbs_g: 250, fat_g: 65, weight_kg: 70 });
  // Body data — used ONLY when we call /api/calculator/macros to re-derive
  // target_kcal after the admin changes the activity level. Prefilled from
  // last calculator result; can be edited by hand.
  const [body, setBody] = useState({ gender: 'male', age: 30, height_cm: 175 });
  const [activityLevel, setActivityLevel] = useState('sedentary_office');
  const [recalculating, setRecalculating] = useState(false);
  const [goal, setGoal] = useState('maintenance');
  const [mode, setMode] = useState('het'); // nap|het|honap
  const [cfg, setCfg] = useState(DEFAULT_CFG());
  const [terv, setTerv] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [existingPlans, setExistingPlans] = useState([]);

  useEffect(() => {
    if (!adminKey) { nav('/admin'); return; }
    axios.get(`${API}/api/admin/training/users`, { headers: { 'X-Admin-Key': adminKey } })
      .then(r => setUsers(r.data.users || []))
      .catch(() => toast.error('Nem sikerült betölteni a felhasználókat.'));
    axios.get(`${API}/api/admin/meal-plans`, { headers: { 'X-Admin-Key': adminKey } })
      .then(r => setExistingPlans(r.data.plans || []))
      .catch(() => {});
  }, [adminKey, nav]);

  // Auto-load calculator target when user selected.
  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/api/admin/meal-plans/users/${userId}/last-calculator`,
      { headers: { 'X-Admin-Key': adminKey } })
      .then(r => {
        const d = r.data;
        if (d.found && d.target) {
          setTarget({
            kcal: d.target.kcal, protein_g: d.target.protein_g,
            carbs_g: d.target.carbs_g, fat_g: d.target.fat_g,
            weight_kg: d.weight_kg || 70,
          });
          if (d.goal && GOAL_MAP[d.goal]) setGoal(d.goal);
          if (d.activity_level && ACTIVITY_MAP[d.activity_level]) setActivityLevel(d.activity_level);
          setBody({
            gender: d.gender || 'male',
            age: d.age || 30,
            height_cm: d.height_cm || 175,
          });
          toast.success('Makró célok + aktivitási szint betöltve a kalkulátorból.');
        } else {
          toast.message('Ehhez az ügyfélhez nincs mentett kalkulátor-eredmény — töltsd ki kézzel a testadatokat + makrócélt.');
        }
      })
      .catch(() => {});
    const user = users.find(u => u.user_id === userId);
    if (user) setTitle(`${mode === 'nap' ? '1 napos' : mode === 'het' ? '7 napos' : 'Havi'} étrend – ${target.kcal} kcal – ${user.name || user.email}`);
  }, [userId]);

  // Recalculate target from body inputs + activity level + goal using the LIVE
  // calculator backend (single source of truth — same formulas end users see).
  const recalcTarget = async () => {
    if (!body.gender || !body.age || !body.height_cm || !target.weight_kg) {
      toast.error('Add meg a nemet, kort, magasságot és a súlyt.'); return;
    }
    setRecalculating(true);
    try {
      const r = await axios.post(`${API}/api/calculator/macros`, {
        gender: body.gender,
        age: +body.age,
        height_cm: +body.height_cm,
        weight_kg: +target.weight_kg,
        activity_level: activityLevel,
        goal,
      });
      const m = r.data;
      setTarget(t => ({
        ...t,
        kcal: m.target_kcal, protein_g: m.protein_g,
        carbs_g: m.carbs_g, fat_g: m.fat_g,
      }));
      toast.success(`Új cél: ${m.target_kcal} kcal (BMR ${m.bmr_kcal} × ${m.activity_factor} = TDEE ${m.tdee_kcal}, cél korrekció ${m.goal_adjustment >= 0 ? '+' : ''}${m.goal_adjustment})`);
    } catch (e) {
      toast.error('Számítás sikertelen: ' + (e?.response?.data?.detail || e.message));
    }
    setRecalculating(false);
  };

  useEffect(() => {
    setTitle(t => {
      if (!t) return t;
      const modeLabel = mode === 'nap' ? '1 napos' : mode === 'het' ? '7 napos' : 'Havi';
      return t.replace(/^(1 napos|7 napos|Havi)/, modeLabel);
    });
  }, [mode]);

  const generate = () => {
    if (!target.kcal || !target.protein_g) { toast.error('Add meg a makrócélokat.'); return; }
    setBusy(true);
    setTimeout(() => {
      try {
        const t = {
          kcal: +target.kcal,
          feherje: +target.protein_g,
          szenhidrat: +target.carbs_g,
          zsir: +target.fat_g,
          suly: +target.weight_kg,
        };
        const goalHu = GOAL_MAP[goal];
        const napok = mode === 'nap' ? 1 : mode === 'het' ? 7 : 28;
        const plan = mode === 'honap' ? generalHonap(t, goalHu, cfg) : generalj(t, goalHu, napok, cfg);
        setTerv(plan);
        toast.success(`Legenerálva: ${plan.length} nap.`);
      } catch (e) {
        toast.error('Hiba a generálás közben: ' + e.message);
      }
      setBusy(false);
    }, 30);
  };

  const save = async (status) => {
    if (!userId) { toast.error('Válassz ügyfelet.'); return; }
    if (!terv.length) { toast.error('Generálj egy tervet előbb.'); return; }
    setSaving(true);
    try {
      const persist = toPersistable(terv);
      const body = {
        user_id: userId,
        title: title || `Étrend – ${target.kcal} kcal`,
        status,
        meta: {
          mode, goal,
          target: { kcal: +target.kcal, protein_g: +target.protein_g, carbs_g: +target.carbs_g, fat_g: +target.fat_g },
          activity_level: activityLevel,
          activity_factor: ACTIVITY_MAP[activityLevel].factor,
          source: 'admin_editor',
          filters: cfg,
        },
        original_plan: persist,
        plan: persist,
      };
      const r = await axios.post(`${API}/api/admin/meal-plans`, body,
        { headers: { 'X-Admin-Key': adminKey } });
      toast.success(`Mentve ${status === 'active' ? '(aktív)' : '(vázlat)'}. ID: ${r.data.id.slice(0, 8)}`);
      setExistingPlans(prev => [r.data, ...prev]);
    } catch (e) {
      toast.error('Mentés sikertelen: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const napiOsszes = useMemo(() => terv.map(d => osszeg(d.meals)), [terv]);
  const havi = useMemo(() => {
    if (!terv.length) return null;
    const n = terv.length;
    const s = napiOsszes.reduce((a, o) => ({ kcal: a.kcal + o.kcal, p: a.p + o.p, ch: a.ch + o.ch, zs: a.zs + o.zs }),
      { kcal: 0, p: 0, ch: 0, zs: 0 });
    return { kcal: s.kcal / n, p: s.p / n, ch: s.ch / n, zs: s.zs / n };
  }, [terv, napiOsszes]);

  if (!adminKey) return null;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-gray-400 hover:text-white">
              <ChevronLeft size={20} />
            </Link>
            <Utensils className="text-yellow-500" size={20} />
            <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Étrend szerkesztő</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex items-start gap-3 text-sm text-amber-100">
          <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Emlékeztető.</strong> Az itt generált terv <strong>általános mintaétrend</strong> — nem dietetikai tanácsadás.
            Krónikus betegség, allergia, terhesség, gyógyszeres kezelés esetén az ügyfelet mindig irányítsd
            kezelőorvoshoz/dietetikushoz. Az ügyfél portál oldalán ez automatikusan megjelenik a terv fölött is.
          </div>
        </div>
        {/* -------- Left: settings -------- */}
        <section className="lg:col-span-1 space-y-4">
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
            <label className="text-xs uppercase tracking-wider text-gray-400 block mb-2">Ügyfél</label>
            <select
              data-testid="meal-plan-user-picker"
              value={userId}
              onChange={e => setUserId(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white"
            >
              <option value="">— Válassz —</option>
              {users.map(u => (
                <option key={u.user_id} value={u.user_id}>{u.name || u.email} ({u.email})</option>
              ))}
            </select>

            <label className="text-xs uppercase tracking-wider text-gray-400 block mt-4 mb-2">Cím</label>
            <Input data-testid="meal-plan-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="pl. 7 napos étrend – 2200 kcal" />
          </div>

          <div className="bg-gray-900 rounded-lg p-4 border border-gray-800 space-y-3">
            <div className="text-xs uppercase tracking-wider text-gray-400">Makrócél</div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-gray-400">Kalória
                <Input type="number" value={target.kcal} onChange={e => setTarget(t => ({ ...t, kcal: +e.target.value }))} data-testid="target-kcal" />
              </label>
              <label className="text-xs text-gray-400">Fehérje (g)
                <Input type="number" value={target.protein_g} onChange={e => setTarget(t => ({ ...t, protein_g: +e.target.value }))} data-testid="target-protein" />
              </label>
              <label className="text-xs text-gray-400">Szénhidrát (g)
                <Input type="number" value={target.carbs_g} onChange={e => setTarget(t => ({ ...t, carbs_g: +e.target.value }))} data-testid="target-carbs" />
              </label>
              <label className="text-xs text-gray-400">Zsír (g)
                <Input type="number" value={target.fat_g} onChange={e => setTarget(t => ({ ...t, fat_g: +e.target.value }))} data-testid="target-fat" />
              </label>
              <label className="text-xs text-gray-400 col-span-2">Súly (kg)
                <Input type="number" value={target.weight_kg} onChange={e => setTarget(t => ({ ...t, weight_kg: +e.target.value }))} />
              </label>
            </div>

            <div>
              <div className="text-xs text-gray-400 mb-1">Cél</div>
              <div className="flex gap-1">
                {Object.entries(GOAL_MAP).map(([k, label]) => (
                  <button
                    key={k}
                    data-testid={`goal-${k}`}
                    onClick={() => setGoal(k)}
                    className={`px-3 py-1.5 text-xs rounded flex-1 border ${goal === k ? 'bg-yellow-500 text-gray-900 border-yellow-500' : 'bg-gray-800 border-gray-700 text-gray-300'}`}
                  >{label}</button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-400 mb-1">Napi aktivitás (munkarend)</div>
              <select
                data-testid="activity-level"
                value={activityLevel}
                onChange={e => setActivityLevel(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white"
              >
                {Object.entries(ACTIVITY_MAP).map(([k, v]) => (
                  <option key={k} value={k}>{v.label} (×{v.factor})</option>
                ))}
              </select>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <label className="text-xs text-gray-400">Nem
                  <select
                    value={body.gender}
                    onChange={e => setBody(b => ({ ...b, gender: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white mt-1"
                  >
                    <option value="male">Férfi</option>
                    <option value="female">Nő</option>
                  </select>
                </label>
                <label className="text-xs text-gray-400">Kor
                  <Input type="number" value={body.age} onChange={e => setBody(b => ({ ...b, age: +e.target.value }))} data-testid="body-age" />
                </label>
                <label className="text-xs text-gray-400">Magasság (cm)
                  <Input type="number" value={body.height_cm} onChange={e => setBody(b => ({ ...b, height_cm: +e.target.value }))} data-testid="body-height" />
                </label>
              </div>
              <Button
                onClick={recalcTarget}
                disabled={recalculating}
                data-testid="btn-recalc-target"
                variant="outline"
                size="sm"
                className="w-full mt-2 text-xs border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10"
              >
                {recalculating ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Wand2 size={12} className="mr-1.5" />}
                Cél újraszámítása (BMR × aktivitás)
              </Button>
            </div>

            <div>
              <div className="text-xs text-gray-400 mb-1">Időtáv</div>
              <div className="flex gap-1">
                {[['nap', '1 nap'], ['het', '7 nap'], ['honap', '4 hét']].map(([k, label]) => (
                  <button
                    key={k}
                    data-testid={`mode-${k}`}
                    onClick={() => setMode(k)}
                    className={`px-3 py-1.5 text-xs rounded flex-1 border ${mode === k ? 'bg-yellow-500 text-gray-900 border-yellow-500' : 'bg-gray-800 border-gray-700 text-gray-300'}`}
                  >{label}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-4 border border-gray-800 space-y-3">
            <div className="text-xs uppercase tracking-wider text-gray-400">Szűrők</div>

            <div className="flex flex-wrap gap-1">
              {[['veget', 'Vegetáriánus'], ['vegan', 'Vegán'], ['laktoz', 'Laktózmentes']].map(([k, l]) => (
                <button
                  key={k}
                  data-testid={`diet-${k}`}
                  onClick={() => setCfg(c => {
                    const next = { ...c, diet: { ...c.diet, [k]: !c.diet[k] } };
                    if (k === 'vegan' && next.diet.vegan) next.diet.veget = true;
                    return next;
                  })}
                  className={`px-3 py-1 text-xs rounded border ${cfg.diet[k] ? 'bg-yellow-500 text-gray-900 border-yellow-500' : 'bg-gray-800 border-gray-700 text-gray-300'}`}
                >{l}</button>
              ))}
            </div>

            <div>
              <div className="text-xs text-gray-400 mb-1">Konyhák (opcionális, több választható)</div>
              <div className="flex flex-wrap gap-1">
                {KONYHAK.map(k => (
                  <button
                    key={k}
                    onClick={() => setCfg(c => ({
                      ...c,
                      konyhak: c.konyhak.includes(k) ? c.konyhak.filter(x => x !== k) : [...c.konyhak, k],
                    }))}
                    className={`px-2.5 py-1 text-xs rounded border ${cfg.konyhak.includes(k) ? 'bg-yellow-500 text-gray-900 border-yellow-500' : 'bg-gray-800 border-gray-700 text-gray-300'}`}
                  >{k}</button>
                ))}
              </div>
            </div>

            <div>
              <button
                onClick={() => setCfg(c => ({ ...c, feherjedus: !c.feherjedus }))}
                className={`px-3 py-1 text-xs rounded border ${cfg.feherjedus ? 'bg-yellow-500 text-gray-900 border-yellow-500' : 'bg-gray-800 border-gray-700 text-gray-300'}`}
              >Csak fehérjedús</button>
            </div>

            {mode === 'het' && (
              <div>
                <div className="text-xs text-gray-400 mb-1">Edzésnapok (extra kcal jut rájuk)</div>
                <div className="flex gap-1">
                  {NAPROV.map((n, i) => (
                    <button
                      key={i}
                      onClick={() => setCfg(c => { const e = [...c.edzes]; e[i] = !e[i]; return { ...c, edzes: e }; })}
                      className={`px-2.5 py-1 text-xs rounded border flex-1 ${cfg.edzes[i] ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-gray-800 border-gray-700 text-gray-300'}`}
                    >{n}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Button
            onClick={generate}
            disabled={busy}
            data-testid="btn-generate"
            className="w-full bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold"
          >
            {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <Wand2 className="mr-2" size={16} />}
            Étrend generálása
          </Button>

          {terv.length > 0 && (
            <div className="flex gap-2">
              <Button
                onClick={() => save('draft')}
                disabled={saving || !userId}
                data-testid="btn-save-draft"
                variant="outline"
                className="flex-1"
              ><Save size={14} className="mr-1" /> Vázlat</Button>
              <Button
                onClick={() => save('active')}
                disabled={saving || !userId}
                data-testid="btn-save-active"
                className="flex-1 bg-emerald-600 hover:bg-emerald-500"
              ><Save size={14} className="mr-1" /> Aktiválás</Button>
            </div>
          )}
        </section>

        {/* -------- Right: preview -------- */}
        <section className="lg:col-span-2 space-y-4">
          {!terv.length ? (
            <div className="bg-gray-900 border border-dashed border-gray-700 rounded-lg p-16 text-center text-gray-500">
              <Utensils size={40} className="mx-auto mb-4 opacity-50" />
              <p>Állítsd be a paramétereket, aztán nyomd meg az „Étrend generálása&rdquo; gombot.</p>
              <p className="text-xs mt-2">A generátor {' '}
                <span className="text-yellow-500">415 receptet</span> és{' '}
                <span className="text-yellow-500">249 alapanyagot</span> használ a háttérben.</p>
            </div>
          ) : (
            <>
              {havi && (
                <div className="bg-gradient-to-br from-yellow-500/10 to-emerald-500/10 border border-yellow-500/30 rounded-lg p-4 grid grid-cols-4 gap-3 text-center">
                  {[['Napi átlag kcal', hu(havi.kcal), +target.kcal],
                    ['Fehérje g', hu(havi.p), +target.protein_g],
                    ['Szénhidrát g', hu(havi.ch), +target.carbs_g],
                    ['Zsír g', hu(havi.zs), +target.fat_g]].map(([label, v, tgt]) => (
                    <div key={label}>
                      <div className="text-xs text-gray-400">{label}</div>
                      <div className="text-lg font-bold text-white">{v}</div>
                      <div className="text-xs text-gray-500">cél: {hu(tgt)}</div>
                    </div>
                  ))}
                </div>
              )}

              {terv.map((d, di) => (
                <div key={di} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-xs uppercase tracking-wider text-gray-400">
                        {mode === 'honap' ? `${d.het + 1}. hét · ` : ''}{mode === 'nap' ? '1. nap' : `${NAPROV[d.i]} nap`}
                      </span>
                      {d.edzes && <span className="ml-2 px-2 py-0.5 bg-emerald-600/20 text-emerald-400 text-xs rounded">edzésnap</span>}
                    </div>
                    <div className="text-xs text-gray-500">cél: {hu(d.cel)} kcal · valós: {hu(napiOsszes[di].kcal)} kcal</div>
                  </div>
                  <div className="space-y-2">
                    {d.meals.map((m, mi) => {
                      const em = emakro(m);
                      const kcal = em.kcal * m.f + (m.koret ? m.koret.kcal * m.koret.g / 100 : 0);
                      return (
                        <div key={mi} className="bg-gray-800/50 rounded px-3 py-2 flex items-start gap-3">
                          <div className="w-20 text-xs text-yellow-500 font-semibold uppercase tracking-wider pt-0.5">{m.slot.cim}</div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-white font-medium">
                              {m.r.nev}
                              {m.batch && <span className="ml-2 text-xs text-emerald-400">(dupla adag)</span>}
                              {m.marad && <span className="ml-2 text-xs text-blue-400">(maradék)</span>}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5 truncate">
                              {alkalmazottHozz(m).map(x => x.qty ? `${x.qty}${x.unit} ${x.nev}` : x.nev).join(', ')}
                              {m.koret && <span className="text-blue-300"> + {m.koret.g}g {m.koret.nev}</span>}
                            </div>
                          </div>
                          <div className="text-xs text-gray-400 text-right whitespace-nowrap">
                            <div>{hu(kcal)} kcal</div>
                            <div className="text-gray-500">×{m.f}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </section>
      </main>

      {existingPlans.length > 0 && (
        <section className="container mx-auto px-6 pb-12">
          <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Elmentett étrendek</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {existingPlans.slice(0, 12).map(p => (
              <div key={p.id} className="bg-gray-900 border border-gray-800 rounded-lg p-3 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded ${p.status === 'active' ? 'bg-emerald-600/20 text-emerald-400' : 'bg-gray-700 text-gray-400'}`}>{p.status}</span>
                  <span className="text-xs text-gray-500">v{p.version}</span>
                </div>
                <div className="font-medium text-white truncate">{p.title}</div>
                <div className="text-xs text-gray-500">{p.user_name || p.user_email}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
